import React from 'react';
import {Field} from 'redux-form';
import parseHydraDocumentation from '@api-platform/api-doc-parser/lib/hydra/parseHydraDocumentation';
import {HydraAdmin} from '@api-platform/admin';
import authProvider from './security/authProvider';
import {Redirect} from 'react-router-dom';
import MarkdownEditor from './components/input/MarkdownEditor';
import appLayout from './components/AppLayout';
import {
    resolveBrowserLocale,
    AutocompleteArrayInput,
    SelectArrayInput,
    SelectInput,
    SelectField
} from 'react-admin';
import '@fortawesome/fontawesome-free-webfonts/css/fontawesome.css';
import '@fortawesome/fontawesome-free-webfonts/css/fa-solid.css';
import dataProvider from './dataProvider';
import translations from './i18n';
import localizationSaga from './sagas/localizationSaga';
import errorSaga from './sagas/errorSaga';
import InputFactory from './components/input/InputFactory';
import ResourceFactory from './ResourceFactory';
import refreshToken from './security/refreshToken';
import jwt from 'jsonwebtoken';
import Dashboard from './components/views/Dashboard';
import UserList from './components/views/UserList';

const entrypoint = process.env.REACT_APP_API_ENTRYPOINT;

const customizeResources = ({api}) => {

    /* Conditions *********************************************************/

    const conditions = api.resources.find(({name}) => 'conditions' === name);
    conditions.listFields = [
        conditions.fields.find(({name}) => 'question' === name),
        conditions.fields.find(({name}) => 'minWeight' === name),
        conditions.fields.find(({name}) => 'maxWeight' === name)
    ];

    /* Users *********************************************************/

    const users = api.resources.find(({name}) => 'users' === name);
    users.list = UserList;
    const userRoles = users.fields.find(({name}) => 'roles' === name);
    const userLang = users.fields.find(({name}) => 'language' === name);
    const choicesLang = [
        {id: 'fr_FR', full_name: 'Français'},
        {id: 'en_US', full_name: 'Anglais US'},
    ];

    userLang.input = props => (
        <SelectInput {...props} choices={choicesLang} optionText="full_name" optionValue="id"/>
    );
    userRoles.input = props => (<SelectArrayInput
        choices={
            [
                {id: 'ROLE_ADMIN_POSTS', name: 'Posts'},
                {id: 'ROLE_ADMIN_TAGS', name: 'Themes, tags, notions, visions'},
                {id: 'ROLE_ADMIN_QUESTIONS', name: 'Questions'},
                {id: 'ROLE_ADMIN_USERS', name: 'Users'},
            ]
        }
        {...props}
    />);

    /* Posts *********************************************************/

    const posts = api.resources.find(({name}) => 'posts' === name);
    const postDescription = posts.fields.find(({name}) => 'description' === name);
    postDescription.input = props => (
        <MarkdownEditor {...props} source='description'/>
    );
    postDescription.field = props => (
        <span>{props.record.description.length > 300 ? props.record.description.substring(0, 297) + '...' : props.record.description}</span>
    );
    postDescription.input.defaultProps = {
        addField: true,
    };

    /* Packs *********************************************************/

    const packs = api.resources.find(({name}) => 'packs' === name);
    const packLang = packs.fields.find(({name}) => 'language' === name);

    packLang.input = props => (
        <SelectInput {...props} choices={choicesLang} optionText="full_name" optionValue="id"/>
    );
    packLang.field = props => (
        <SelectField {...props} choices={choicesLang} optionText="full_name" optionValue="id"/>
    );

    /* UserAnswers ***************************************************/

    const userAnswer = api.resources.find(({name}) => 'user_answers' === name);
    const userAnswerGaugeLevel = userAnswer.fields.find(({name}) => 'gaugeLevel' === name);
    userAnswerGaugeLevel.inputProps = {options: {max: 5, min: 0}};

    /* AnswerGroups **************************************************/

    const answerGroup = api.resources.find(({name}) => 'answer_groups' === name);
    answerGroup.listFields = [
      answerGroup.fields.find(({name}) => 'label' === name),
      answerGroup.fields.find(({name}) => 'answers' === name),
    ];

    const answerGroupAnswers = answerGroup.fields.find(({name}) => 'answers' === name);
    answerGroupAnswers.inputProps = {refProps: {options: {newChipKeyCodes: []}}};
    const answerGroupNewAnswers = answerGroup.fields.find(({name}) => 'newAnswers' === name);
    answerGroupNewAnswers.input = props => (
        <Field name='newAnswers' {...props} component={
            props => <AutocompleteArrayInput defaultValue={[]} {...props} options={{newChipKeyCodes: [190, 13]}} choices={[]}/>
        }></Field>
    );
    const answerGroupLabel = answerGroup.fields.find(({name}) => 'label' === name);
    answerGroupLabel.fieldProps = {sortable: false};

    /* Global *********************************************************/

    const autocompleteList = {
        questions: ['theme', 'vision', 'notion', 'pack', 'tags', 'condition'],
        answer_groups: ['answers'],
        conditions: ['question']
    };
    api.resources.map(resource => {
        if (autocompleteList.hasOwnProperty(resource.name)) {
            resource.writableFields.map(field => {
                if (autocompleteList[resource.name].indexOf(field.name) !== -1) {
                    field.inputProps = {...field.inputProps, autocomplete: true};
                    if (field.name === 'condition') {
                        field.inputProps.searchBy = 'question.label';
                        // field.inputProps.defaultValue = '';
                    }
                    if (field.name === 'tags') {
                        field.inputProps.defaultValue = [];
                    }
                }
                return null;
            })
        }
        return null;
    });

    return {api}
};

const documentationParseError = (result) => {

    switch (result.status) {
        case 401:
            return Promise.resolve({
                api: result.api,
                customRoutes: [{
                    props: {
                        path: '/',
                        render: () => <Redirect to={{pathname: '/login'}}/>,
                    },
                }],
            });

        default:
            return Promise.reject(result);
    }
};

const apiDocumentationParser = async entrypoint => {
    let decodedToken = jwt.decode(localStorage.getItem('token'));
    if (!decodedToken || decodedToken.exp < (Math.floor(Date.now() / 1000)) + 5) {
        try {
            await refreshToken();
        } catch (err) {

        }
    }

    const fetchHeaders = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Admin-Request': 'true'
    };
    return parseHydraDocumentation(entrypoint, {headers: new Headers(fetchHeaders)})
        .then(
            (params) => customizeResources(params),
            (params) => documentationParseError(params)
        );
};

const i18nProvider = locale => {
    if (translations[locale] !== undefined) {
        return translations[locale];
    }
    return translations['en_US'];
};

const language = localStorage.getItem('language');
export default props => (
    <HydraAdmin
        dashboard={Dashboard}
        inputFactory={InputFactory}
        customSagas={[localizationSaga, errorSaga]}
        locale={language ? language : resolveBrowserLocale()}
        i18nProvider={i18nProvider}
        apiDocumentationParser={apiDocumentationParser}
        authProvider={authProvider}
        entrypoint={entrypoint}
        dataProvider={dataProvider}
        resourceFactory={ResourceFactory}
        appLayout={appLayout}
    />
)



// WEBPACK FOOTER //
// ./src/App.js